import axios from "axios"
import { useEffect, useMemo, useRef, useState } from "react"
import { Link, useParams } from "react-router-dom"
import Footer from "../../components/Footer/Footer.jsx"
import Form from "../../components/Form/Form"
import "../../components/Form/ModalForm.css"
import { ProductDetailSkeleton } from "./ProductSkeletons"
import "./Product.css"

function parseJsonArray(value) {
    if (!value) return []

    if (Array.isArray(value)) return value

    try {
        const parsed = JSON.parse(value)
        return Array.isArray(parsed) ? parsed : []
    } catch {
        return []
    }
}

function parseJsonObject(value) {
    if (!value) return null

    if (typeof value === "object") return value

    try {
        const parsed = JSON.parse(value)
        return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : null
    } catch {
        return null
    }
}

function normalizeGalleryItem(item, index) {
    if (typeof item === "string") {
        return {
            src: item,
            title: `Экран ${index + 1}`,
            caption: "",
            fit: "contain",
        }
    }

    return {
        src: item?.src || item?.image || item?.url || "",
        title: item?.title || item?.category || `Экран ${index + 1}`,
        caption: item?.caption || item?.description || "",
        fit: item?.fit === "cover" ? "cover" : "contain",
    }
}

function hasProductPage(product) {
    return Boolean(
        product?.page_title ||
        product?.page_summary ||
        product?.page_content ||
        product?.page_images ||
        product?.features?.length ||
        product?.page_use_cases ||
        product?.page_cta
    )
}

function isPublicProduct(product) {
    return !product?.hidden
}

function formatPrice(value) {
    return new Intl.NumberFormat("ru-RU").format(value || 0)
}

function getFeatureCards(product) {
    return (product?.features ?? [])
        .filter((feature) => feature.name || feature.description)
        .slice(0, 6)
        .map((feature, index) => ({
            icon: feature.icon || String(index + 1).padStart(2, "0"),
            title: feature.title || feature.name,
            description: feature.description,
        }))
}

export default function ProductDetailPage() {
    const { productId } = useParams()
    const resumeAutoplayRef = useRef(null)
    const [products, setProducts] = useState([])
    const [isLoading, setIsLoading] = useState(true)
    const [activeImageIndex, setActiveImageIndex] = useState(0)
    const [isAutoplayPaused, setIsAutoplayPaused] = useState(false)
    const [isViewerOpen, setIsViewerOpen] = useState(false)
    const [isDemoOpen, setIsDemoOpen] = useState(false)

    useEffect(() => {
        async function fetchProducts() {
            try {
                const response = await axios.get("/api/products")
                setProducts((response.data.data ?? []).filter(isPublicProduct))
            } catch (error) {
                console.error("Ошибка при загрузке страницы продукта", error)
            } finally {
                setIsLoading(false)
            }
        }

        fetchProducts()
    }, [])

    useEffect(() => {
        if (!isDemoOpen) return undefined

        const handleEscape = (event) => {
            if (event.key === "Escape") setIsDemoOpen(false)
        }

        document.addEventListener("keydown", handleEscape)
        return () => document.removeEventListener("keydown", handleEscape)
    }, [isDemoOpen])

    const product = useMemo(
        () => products.find((item) => String(item.key) === String(productId)),
        [products, productId]
    )

    const gallery = useMemo(
        () => parseJsonArray(product?.page_images).map(normalizeGalleryItem).filter((item) => item.src),
        [product]
    )
    const featureCards = useMemo(() => getFeatureCards(product), [product])
    const useCases = useMemo(() => parseJsonArray(product?.page_use_cases), [product])
    const finalCta = useMemo(() => parseJsonObject(product?.page_cta), [product])
    const activeSlide = gallery[activeImageIndex]

    useEffect(() => {
        setActiveImageIndex(0)
    }, [productId, gallery.length])

    useEffect(() => {
        if (gallery.length < 2 || isAutoplayPaused || isViewerOpen) return undefined

        const intervalId = window.setInterval(() => {
            setActiveImageIndex((current) => (current + 1) % gallery.length)
        }, 4500)

        return () => window.clearInterval(intervalId)
    }, [gallery.length, isAutoplayPaused, isViewerOpen])

    useEffect(() => (
        () => window.clearTimeout(resumeAutoplayRef.current)
    ), [])

    const pauseAutoplay = () => {
        setIsAutoplayPaused(true)
        window.clearTimeout(resumeAutoplayRef.current)
        resumeAutoplayRef.current = window.setTimeout(() => {
            setIsAutoplayPaused(false)
        }, 4000)
    }

    const showImage = (nextIndex) => {
        if (gallery.length === 0) return

        pauseAutoplay()
        setActiveImageIndex((nextIndex + gallery.length) % gallery.length)
    }

    const showPreviousImage = () => showImage(activeImageIndex - 1)
    const showNextImage = () => showImage(activeImageIndex + 1)

    if (isLoading) {
        return <ProductDetailSkeleton />
    }

    if (!product || !hasProductPage(product)) {
        return (
            <>
                <main className="product productDetail">
                    <Link className="productDetail__back" to="/product">Назад к продуктам</Link>
                    <p className="product__message">Страница продукта пока не заполнена.</p>
                </main>
                <Footer />
            </>
        )
    }

    const isInfoOnly = Boolean(product.info_only)

    return (
        <>
            <main className="product productDetail">
                <section className="productDetail__hero">
                    <div className="productDetail__heroMeta">
                        <Link className="productDetail__back" to="/product">Назад к продуктам</Link>
                        <span className="productDetail__eyebrow">Продукт экосистемы</span>
                    </div>

                    <div className="productDetail__titleLine">
                        <div className="productDetail__titleContent">
                            <h1>{product.page_title || product.module}</h1>
                            {product.image && (
                                <img className="productDetail__titleIcon" src={product.image} alt="" aria-hidden="true" />
                            )}
                        </div>
                    </div>

                    <div className="productDetail__heroCopy">
                        <p>{product.page_summary || product.design}</p>
                        <div className="productDetail__heroActions">
                            {!isInfoOnly && (
                                <Link className="productDetail__primaryAction" to="/product/calculator">
                                    Рассчитать стоимость
                                </Link>
                            )}
                            <button className="productDetail__secondaryAction" type="button" onClick={() => setIsDemoOpen(true)}>
                                {isInfoOnly ? "Узнать о продукте" : "Запросить демонстрацию"}
                            </button>
                        </div>
                    </div>

                    <aside className="productDetail__heroPanel" aria-label="Краткая информация о продукте">
                        {isInfoOnly ? (
                            <div className="productDetail__priceCard">
                                <span>Статус</span>
                                <strong>Скоро в продаже</strong>
                                <small>Информация о продукте уже доступна</small>
                            </div>
                        ) : (
                            <div className="productDetail__priceCard">
                                <span>Стоимость</span>
                                <strong>от {formatPrice(product.price)} ₽</strong>
                                <small>за пользователя</small>
                            </div>
                        )}
                    </aside>
                </section>

                {gallery.length > 0 && (
                    <section className="productDetail__showcase">
                        <div className="productDetail__sectionHeader">
                            <span>Демонстрация</span>
                            <h2>Интерфейс продукта</h2>
                            <p>Посмотрите, как выглядит рабочая среда продукта в реальных экранах.</p>
                        </div>

                        <div className="productDetail__browserFrame">
                            <div className="productDetail__browserBar">
                            <span className="productDetail__browserDots" aria-hidden="true">
                                <i />
                                <i />
                                <i />
                            </span>
                                <strong>{activeSlide?.title}</strong>
                                <span>{activeImageIndex + 1} / {gallery.length}</span>
                            </div>

                            <div className="productDetail__slider" aria-label="Скриншоты продукта">
                                <button
                                    className="productDetail__sliderArrow productDetail__sliderArrow_left"
                                    type="button"
                                    onClick={showPreviousImage}
                                    aria-label="Предыдущий скриншот"
                                >
                                    ‹
                                </button>
                                <button
                                    className="productDetail__sliderImage"
                                    type="button"
                                    onClick={() => {
                                        pauseAutoplay()
                                        setIsViewerOpen(true)
                                    }}
                                    aria-label="Открыть скриншот на весь экран"
                                >
                                <span
                                    className="productDetail__sliderTrack"
                                    style={{ transform: `translateX(-${activeImageIndex * 100}%)` }}
                                >
                                    {gallery.map((image, index) => (
                                        <span className="productDetail__sliderSlide" key={`${image.src.slice(0, 32)}-${index}`}>
                                            <img
                                                className={image.fit === "cover" ? "productDetail__slideImage productDetail__slideImage_cover" : "productDetail__slideImage"}
                                                src={image.src}
                                                alt={image.title}
                                            />
                                        </span>
                                    ))}
                                </span>
                                </button>
                                <button
                                    className="productDetail__sliderArrow productDetail__sliderArrow_right"
                                    type="button"
                                    onClick={showNextImage}
                                    aria-label="Следующий скриншот"
                                >
                                    ›
                                </button>
                            </div>
                        </div>

                        {(activeSlide?.caption || gallery.length > 1) && (
                            <div className="productDetail__sliderMeta">
                                {activeSlide?.caption && <p>{activeSlide.caption}</p>}
                                {gallery.length > 1 && (
                                    <div className="productDetail__sliderDots">
                                        {gallery.map((image, index) => (
                                            <button
                                                className={index === activeImageIndex ? "productDetail__sliderDot productDetail__sliderDot_active" : "productDetail__sliderDot"}
                                                type="button"
                                                key={`${image.src.slice(0, 32)}-${index}`}
                                                onClick={() => showImage(index)}
                                                aria-label={`Скриншот ${index + 1}`}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </section>
                )}

                {featureCards.length > 0 && (
                    <section className="productDetail__valueSection">
                        <div className="productDetail__sectionHeader">
                            <span>Возможности</span>
                            <h2>Что получает команда</h2>
                        </div>
                        <div className="productDetail__featureGrid">
                            {featureCards.map((feature, index) => (
                                <article className="productDetail__featureCard" key={`${feature.title}-${index}`}>
                                    <span>{feature.icon}</span>
                                    <h3>{feature.title}</h3>
                                    {feature.description && <p>{feature.description}</p>}
                                </article>
                            ))}
                        </div>
                    </section>
                )}

                {useCases.length > 0 && (
                    <section className="productDetail__valueSection">
                        <div className="productDetail__sectionHeader">
                            <span>Сценарии</span>
                            <h2>Где продукт особенно полезен</h2>
                        </div>
                        <div className="productDetail__useCaseGrid">
                            {useCases.slice(0, 3).map((item, index) => (
                                <article className="productDetail__useCaseCard" key={`${item.title}-${index}`}>
                                    <h3>{item.title}</h3>
                                    {item.description && <p>{item.description}</p>}
                                </article>
                            ))}
                        </div>
                    </section>
                )}

                {product.page_content && (
                    <section className="productDetail__content">
                        <span className="productDetail__contentLabel">Описание</span>
                        <h2>{product.page_title || product.module}</h2>
                        {product.page_content.split("\n").filter(Boolean).map((paragraph, index) => (
                            <p key={`${paragraph.slice(0, 24)}-${index}`}>{paragraph}</p>
                        ))}
                    </section>
                )}

                {finalCta && (
                    <section className="productDetail__finalCta">
                        <div>
                            <span>Следующий шаг</span>
                            <h2>{finalCta.title || "Подберите конфигурацию продукта"}</h2>
                            {finalCta.description && <p>{finalCta.description}</p>}
                        </div>
                        <div className="productDetail__finalActions">
                            {!isInfoOnly && (
                                <Link className="productDetail__primaryAction" to="/product/calculator">
                                    {finalCta.primaryButtonText || "Рассчитать стоимость"}
                                </Link>
                            )}
                            <button className="productDetail__secondaryAction" type="button" onClick={() => setIsDemoOpen(true)}>
                                {finalCta.secondaryButtonText || (isInfoOnly ? "Узнать о продукте" : "Запросить демонстрацию")}
                            </button>
                        </div>
                    </section>
                )}

                {isViewerOpen && gallery.length > 0 && (
                    <div className="productDetail__viewer" role="dialog" aria-modal="true">
                        <button className="productDetail__viewerClose" type="button" onClick={() => setIsViewerOpen(false)}>
                            Закрыть
                        </button>
                        <button
                            className="productDetail__viewerArrow productDetail__viewerArrow_left"
                            type="button"
                            onClick={showPreviousImage}
                            aria-label="Предыдущий скриншот"
                        >
                            ‹
                        </button>
                        <img src={gallery[activeImageIndex].src} alt={gallery[activeImageIndex].title} />
                        <button
                            className="productDetail__viewerArrow productDetail__viewerArrow_right"
                            type="button"
                            onClick={showNextImage}
                            aria-label="Следующий скриншот"
                        >
                            ›
                        </button>
                    </div>
                )}

                {isDemoOpen && (
                    <div className="modal_form_overlay" role="presentation" onClick={() => setIsDemoOpen(false)}>
                        <div
                            className="modal_form_content"
                            role="dialog"
                            aria-modal="true"
                            aria-label="Запросить демонстрацию"
                            onClick={(event) => event.stopPropagation()}
                        >
                            <button className="modal_form_close" type="button" onClick={() => setIsDemoOpen(false)} aria-label="Закрыть форму">
                                ×
                            </button>
                            <Form requestType="Заявка на демонстрацию" messagePrefix={`Продукт: ${product.module}`} submitText="Отправить заявку" successText="Заявка отправлена" />
                        </div>
                    </div>
                )}
            </main>
            <Footer />
        </>
    )
}
